/* eslint-disable react-refresh/only-export-components */
import Keycloak from 'keycloak-js';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { restApiUrl } from '../helpers/backendUrl';

interface KeycloakContextType {
  keycloak: Keycloak | null;
  authenticated: boolean;
  initialized: boolean;
  token: string | undefined;
  login: (redirectUri?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

const KeycloakContext = createContext<KeycloakContextType>({
  keycloak: null,
  authenticated: false,
  initialized: false,
  token: undefined,
  login: async () => {},
  logout: async () => {},
  refreshToken: async () => false,
});

export const useKeycloak = () => useContext(KeycloakContext);

interface KeycloakProviderProps {
  children: React.ReactNode;
}

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
};

const isKeycloakEnabled = () =>
  Boolean(
    keycloakConfig.url && keycloakConfig.realm && keycloakConfig.clientId,
  );

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({
  children,
}) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(!isKeycloakEnabled());
  const [token, setToken] = useState<string | undefined>();
  const initRef = useRef(false);

  useEffect(() => {
    if (!isKeycloakEnabled() || initRef.current) return;
    initRef.current = true;

    const kc = new Keycloak({
      url: keycloakConfig.url!,
      realm: keycloakConfig.realm!,
      clientId: keycloakConfig.clientId!,
    });

    kc.onTokenExpired = () => {
      kc.updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            setToken(kc.token);
          }
        })
        .catch(() => {
          setAuthenticated(false);
          setToken(undefined);
          if (window.location.pathname.startsWith('/admin')) {
            kc.login({ redirectUri: window.location.href });
          }
        });
    };

    // Keep React state in sync whenever Keycloak refreshes the token from any
    // path (auto-refresh, manual refreshToken() call, etc.) so urql picks up
    // the new Bearer header.
    kc.onAuthRefreshSuccess = () => {
      setToken(kc.token);
    };
    kc.onAuthRefreshError = () => {
      setAuthenticated(false);
      setToken(undefined);
    };

    kc.onAuthLogout = () => {
      setAuthenticated(false);
      setToken(undefined);
    };

    kc.init({ onLoad: 'check-sso', pkceMethod: 'S256' })
      .then((auth) => {
        setKeycloak(kc);
        setAuthenticated(auth);
        setToken(kc.token);
        setInitialized(true);
        // if (auth && kc.token) {
        //   console.log('[Keycloak] JWT:', kc.token);
        //   console.log('[Keycloak] Parsed:', kc.tokenParsed);
        // }
      })
      .catch((err) => {
        console.error(
          '[Keycloak] Init failed, falling back to unauthenticated:',
          err,
        );
        setInitialized(true);
      });
  }, []);

  const login = useCallback(
    async (redirectUri?: string) => {
      const target = redirectUri || window.location.href;
      if (keycloak) {
        await keycloak.login({ redirectUri: target });
      } else {
        // Fallback: session-based login
        window.location.href = restApiUrl(
          `/auth/login?successRef=${encodeURIComponent(target)}`,
        );
      }
    },
    [keycloak],
  );

  const logout = useCallback(async () => {
    if (keycloak) {
      await keycloak.logout({ redirectUri: window.location.origin });
    } else {
      window.location.href = restApiUrl('/auth/logout');
    }
  }, [keycloak]);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!keycloak) return false;
    const refreshed = await keycloak.updateToken(-1);
    // onAuthRefreshSuccess fires when refreshed === true, but call setToken
    // unconditionally so callers can rely on the context being current the
    // moment this Promise resolves rather than waiting on the event loop.
    setToken(keycloak.token);
    return refreshed;
  }, [keycloak]);

  return (
    <KeycloakContext.Provider
      value={{
        keycloak,
        authenticated,
        initialized,
        token,
        login,
        logout,
        refreshToken,
      }}
    >
      {initialized ? children : null}
    </KeycloakContext.Provider>
  );
};
